import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { slotPricingApi } from '../services/api';
import { SlotPricingTemplate, SlotPricingTemplateCreate, SlotPricingTemplateUpdate } from '../types';
import { DollarSign, Edit2, Trash2, Plus, X, AlertCircle } from 'lucide-react';
import { formatCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities

const SlotPricingManager: React.FC = () => {
  const { fairId } = useParams<{ fairId: string }>();
  const [templates, setTemplates] = useState<SlotPricingTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<SlotPricingTemplate | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);

  // Form state - separate types for create vs edit for type safety
  const [createFormData, setCreateFormData] = useState<SlotPricingTemplateCreate>({
    fair_id: parseInt(fairId || '0'),
    slot_type: '',
    base_price: 0,
    template_name: '',
    description: ''
  });

  const [editFormData, setEditFormData] = useState<SlotPricingTemplateUpdate>({
    base_price: 0,
    template_name: '',
    description: ''
  });

  const loadTemplates = useCallback(async () => {
    if (!fairId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await slotPricingApi.getTemplates(parseInt(fairId), 1, 100);
      setTemplates(response.items);
    } catch (err: unknown) {
      setError('Failed to load pricing templates');
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  }, [fairId]);

  useEffect(() => {
    if (fairId) {
      loadTemplates();
    }
  }, [fairId, loadTemplates]);

  const handleCreate = async () => {
    if (!fairId || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      const createData = { ...createFormData, fair_id: parseInt(fairId) };
      await slotPricingApi.createTemplate(createData);
      setShowCreateModal(false);
      resetForm();
      loadTemplates();
    } catch (err: unknown) {
      setError('Failed to create pricing template');
      console.error('Error creating template:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingTemplate || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      await slotPricingApi.updateTemplate(editingTemplate.id, editFormData);
      setEditingTemplate(null);
      resetForm();
      loadTemplates();
    } catch (err: unknown) {
      setError('Failed to update pricing template');
      console.error('Error updating template:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (templateId: number) => {
    try {
      await slotPricingApi.deleteTemplate(templateId);
      setShowDeleteConfirm(null);
      loadTemplates();
    } catch (err: unknown) {
      setError('Failed to delete pricing template');
      console.error('Error deleting template:', err);
    }
  };

  const resetForm = () => {
    setCreateFormData({
      fair_id: parseInt(fairId || '0'),
      slot_type: '',
      base_price: 0,
      template_name: '',
      description: ''
    });
    setEditFormData({
      base_price: 0,
      template_name: '',
      description: ''
    });
  };

  const openEditModal = (template: SlotPricingTemplate) => {
    setEditingTemplate(template);
    setEditFormData({
      base_price: template.base_price,
      template_name: template.template_name || '',
      description: template.description || ''
    });
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setEditingTemplate(null);
    resetForm();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Slot Pricing Templates</h2>
        <button
          data-testid="create-pricing-template-button"
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
        >
          <Plus className="w-5 h-5" />
          Create Template
        </button>
      </div>

      {error && (
        <div data-testid="pricing-error-message" className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {templates.length === 0 ? (
        <div data-testid="pricing-empty-state" className="text-center py-12 bg-gray-50 rounded-lg">
          <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">No pricing templates yet</p>
          <button
            data-testid="create-first-template-button"
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
          >
            Create Your First Template
          </button>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <table data-testid="pricing-template-list" className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Slot Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Template Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Base Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {templates.map((template) => (
                <tr key={template.id} data-testid="pricing-template-item" className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-sm font-medium bg-purple-100 text-purple-800 rounded">
                      {template.slot_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {template.template_name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1 text-sm font-semibold text-green-600">
                      <DollarSign className="w-4 h-4" />
                      {formatCurrency(template.base_price).replace('$', '')} {/* Issue #905: Decimal.js formatting */}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                    {template.description || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      data-testid="edit-template-button"
                      onClick={() => openEditModal(template)}
                      className="text-secondary hover:text-teal-900 mr-3"
                      title="Edit template"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      data-testid="delete-template-button"
                      onClick={() => setShowDeleteConfirm(template.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete template"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingTemplate) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div data-testid="pricing-template-modal" className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">
                {editingTemplate ? 'Edit Pricing Template' : 'Create Pricing Template'}
              </h3>
              <button data-testid="pricing-modal-close-button" onClick={closeModals} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {!editingTemplate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slot Type * (cannot be changed after creation)
                  </label>
                  <input
                    data-testid="slot-type-input"
                    type="text"
                    value={createFormData.slot_type}
                    onChange={(e) => setCreateFormData({ ...createFormData, slot_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                    placeholder="e.g., common, premium, vip"
                    required
                    disabled={submitting}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Base Price * ($)
                </label>
                <input
                  data-testid="base-price-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editingTemplate ? editFormData.base_price : createFormData.base_price}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    if (editingTemplate) {
                      setEditFormData({ ...editFormData, base_price: value });
                    } else {
                      setCreateFormData({ ...createFormData, base_price: value });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  required
                  disabled={submitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name
                </label>
                <input
                  data-testid="template-name-input"
                  type="text"
                  value={editingTemplate ? editFormData.template_name : createFormData.template_name}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (editingTemplate) {
                      setEditFormData({ ...editFormData, template_name: value });
                    } else {
                      setCreateFormData({ ...createFormData, template_name: value });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Standard Booth Pricing"
                  disabled={submitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  data-testid="template-description-input"
                  value={editingTemplate ? editFormData.description : createFormData.description}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (editingTemplate) {
                      setEditFormData({ ...editFormData, description: value });
                    } else {
                      setCreateFormData({ ...createFormData, description: value });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  rows={3}
                  placeholder="Optional description of this pricing template"
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                data-testid="pricing-cancel-button"
                onClick={closeModals}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                data-testid="pricing-save-button"
                onClick={editingTemplate ? handleUpdate : handleCreate}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : editingTemplate ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div data-testid="delete-template-modal" className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Confirm Deletion</h3>
            <p data-testid="delete-template-message" className="text-gray-600 mb-6">
              Are you sure you want to delete this pricing template? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                data-testid="delete-cancel-button"
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                data-testid="delete-confirm-button"
                onClick={() => handleDelete(showDeleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SlotPricingManager;
